import React,{ useCallback, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { LinearGradient } from 'expo-linear-gradient';
import i18n from 'i18n-js';
import { fetchProductsForGrid } from '../../utils/productGridApi';
import ServerPaginatedDataGrid from '../../commons/ServerPaginatedDataGrid';
import { productGridColumns, productBulkActions, productFilterOptions, typeFilterOptions } from './productGridConfig';
import ConfirmationDialog from '../../commons/modal/ConfirmationDialog';
import useProductBulkActions from './hooks/useProductBulkActions';
import Toast from '../../commons/Toast';
import { CreateProduct, SetIsProductCreated } from '../../actions/productAction';

const PRODUCT_SEARCH_GRID_ID = 'productSearchGrid';

const ProductSearchGrid = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.user?.userInfo);
  const isProductCreated = useSelector(state => state.product?.isProductCreated);
  const productId = useSelector(state => state.product?.productId);
  const {
    confirmModal,
    refreshKey,
    handleBulkAction,
    handleConfirmAction,
    handleCancelConfirm,
    showToast,
    toastMessage,
    hideToast
  } = useProductBulkActions();

  const fetchProducts = useCallback(async ({ skip, limit, sortInfo, searchQuery, filter, is_kit }) => {
    return fetchProductsForGrid({ skip, limit, sortInfo, searchQuery, filter, is_kit });
  }, []);

  const handleRowClick = useCallback((rowData, productsList, currentIndex) => {
    navigation.navigate('ProductDetail', {
      productInfo: rowData.id,
      page: 'ProductSearchGrid',
      productsList: productsList,
      currentProductIndex: currentIndex
    });
  }, [navigation]);

  const handleCreateProduct = useCallback(() => {
    dispatch(CreateProduct());
  }, [dispatch]);

  useEffect(() => {
    if (isProductCreated === false && productId) {
      dispatch(SetIsProductCreated());
      navigation.navigate('ProductDetail', {
        productInfo: productId,
        page: 'ProductSearchGrid'
      });
    }
  }, [isProductCreated, productId, dispatch, navigation]);

  return (
    <LinearGradient
      colors={['#000', '#000', '#5c778f', '#5c778f', '#253c57', '#253c57', '#000']}
      start={[-0.5, -1]}
      style={styles.container}
    >
      <View style={styles.gridContainer}>
        <ServerPaginatedDataGrid
          columns={productGridColumns}
          fetchData={fetchProducts}
          searchPlaceholder={i18n.t('productGrid.searchPlaceholder')}
          bulkActions={productBulkActions}
          onBulkAction={handleBulkAction}
          onRowClick={handleRowClick}
          idProperty="id"
          refreshKey={refreshKey}
          filterOptions={productFilterOptions}
          defaultFilter="active"
          typeFilterOptions={typeFilterOptions}
          defaultTypeFilter="all"
          gridId={PRODUCT_SEARCH_GRID_ID}
          userInfo={userInfo}
          toolbarRight={
            <TouchableOpacity
              testID="createProductButton"
              style={styles.createButton}
              onPress={handleCreateProduct}
            >
              <Text style={styles.createButtonText}>
                {i18n.t('productGrid.createProduct')}
              </Text>
            </TouchableOpacity>
          }
        />
      </View>

      {confirmModal.visible && (
        <ConfirmationDialog
          title={i18n.t('productGrid.deleteConfirmTitle')}
          message={confirmModal.message}
          confirmText={i18n.t('productGrid.deleteConfirmYes')}
          cancelText={i18n.t('productGrid.cancel')}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelConfirm}
          onClose={handleCancelConfirm}
        />
      )}

      <Toast
        visible={showToast}
        message={toastMessage}
        onHide={hideToast}
        duration={5000}
      />
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%'
  },
  gridContainer: {
    flex: 1,
    padding: 10
  },
  createButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#fff',
    borderRadius: 30,
    height: 22
  },
  createButtonText: {
    color: 'white',
    paddingHorizontal: 10,
    fontWeight: 'bold',
    fontSize: 12
  }
});

export default ProductSearchGrid;
